/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import PropTypes from 'prop-types';
import { at } from 'lodash';
import { useField, useFormikContext } from 'formik';
import {
  FormControl, FormHelperText, RadioGroup, Radio, FormControlLabel, FormLabel,
} from '@mui/material';
import { useCheckoutStore } from '../../Hooks/Zustand/useCheckoutStore';

/**
 * Form RadioField Component
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export function RadioField(props) {
  const { label, data, ...rest } = props;
  const [field, meta] = useField(props);
  const { value: selectedValue } = field;
  const [touched, error] = at(meta, 'touched', 'error');
  const isError = touched && error && true;
  const { updateFormDataValue, setLastValidStep, getActiveStep } = useCheckoutStore();
  const { handleChange } = useFormikContext();

  /**
     * Handle error
     * @returns {React.JSX.Element|null}
     * @private
     */
  function _renderHelperText() {
    if (isError) {
      return <FormHelperText>{error}</FormHelperText>;
    }
    return null;
  }

  return (
    <FormControl {...rest} error={isError}>
      <FormLabel>{label}</FormLabel>
      <RadioGroup
        sx={{ flexDirection: 'row' }}
        {...field}
        value={selectedValue || ''}
        onChange={(event) => {
          handleChange(event);
          updateFormDataValue(field.name, field.value);
          setLastValidStep(getActiveStep());
        }}
      >
        {data.map((item) => (
          <FormControlLabel control={<Radio />} key={item.value} label={item.label} value={item.value} />
        ))}
      </RadioGroup>
      {_renderHelperText()}
    </FormControl>
  );
}

RadioField.propTypes = {
  label: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
};
